import useLocalization from '@app/hooks/useLocalization'
import {
  IReferenceBillingTariff,
  IReferenceBillingTariffGroup,
} from '@app/interfaces/reference-billing'
import {
  useGetBillingTariffsQuery,
  useUpdateTariffStatusMutation,
} from '@app/store/rtk/api/billing-tariffs-api'
import { setDocumentTitle } from '@app/utils/set-document-title/set-document-title'
import { Button, Card, Spin, Switch, Table, Tag } from 'antd'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddBillingTariffModal } from './components/add-billing-tariff-modal/add-billing-tariff-modal'
import { UpdateBillingTariffModal } from './components/update-billing-tariff-modal/update-billing-tariff-modal'
import './reference-book-billing-page.less'
import { rolesChecker } from '@app/utils/roles-checker/rolesChecker'
import { AUTHORITIES } from '@app/constants/authorities'
import { TariffChangeHistory } from './components/tariff-change-history/tariff-change-history'
import ThreeDots from '@app/shared/loader/ThreeDots'
import { formatTiyinToSumLabel } from '@app/utils/format-tiyin-to-sum-label'

const permissions = [AUTHORITIES.ADMIN]

const ReferenceBookBillingPage: React.FC = () => {
  const { t } = useTranslation()
  const [view, setView] = useState<'tariffs' | 'change-history'>('tariffs')
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [updateModalVisible, setUpdateModalVisible] = useState(false)
  const [selectedTariff, setSelectedTariff] =
    useState<IReferenceBillingTariff | null>(null)
  const canManageTariff = rolesChecker(permissions)

  setDocumentTitle(t('general.referencePageTitle'))

  const { data: tariffsData } = useGetBillingTariffsQuery()
  const mappedTariffs: Record<number, IReferenceBillingTariffGroup> =
    useMemo(() => {
      return (
        tariffsData?.response?.reduce((acc, tariff) => {
          if (acc[tariff.organizationLocality.id]) {
            acc[tariff.organizationLocality.id].tariffs.push(tariff)
          } else {
            acc[tariff.organizationLocality.id] = {
              localityData: tariff.organizationLocality,
              tariffs: [tariff],
            }
          }
          return acc
        }, {} as Record<number, IReferenceBillingTariffGroup>) ?? {}
      )
    }, [tariffsData])

  const onEditOneTariff = (tariff: IReferenceBillingTariff) => {
    setSelectedTariff(tariff)
    setUpdateModalVisible(true)
  }

  const onSeeChangeHistory = (tariff: IReferenceBillingTariff) => {
    setSelectedTariff(tariff)
    setView('change-history')
  }

  const renderTabs = useMemo(() => {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Button
          onClick={() => {
            setSelectedTariff(null)
            setView('tariffs')
          }}
          type={view === 'tariffs' ? 'primary' : 'default'}>
          {t('billing.tariffs')}
        </Button>

        <Button
          onClick={() => {
            setSelectedTariff(null)
            setView('change-history')
          }}
          type={view === 'change-history' ? 'primary' : 'default'}>
          {t('billing.changeHistory')}
        </Button>
      </div>
    )
  }, [view])

  return (
    <>
      <Card
        title={canManageTariff ? renderTabs : t('billing.tariffs')}
        extra={
          canManageTariff && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button type="primary" onClick={() => setAddModalVisible(true)}>
                {t('billing.add_tariff')}
              </Button>
            </div>
          )
        }>
        {view === 'tariffs' &&
          Object.values(mappedTariffs).map((localityData, index) => {
            return (
              <LocalityTariffTable
                key={index}
                data={localityData}
                onSeeChangeHistory={onSeeChangeHistory}
                onEditOneTariff={onEditOneTariff}
              />
            )
          })}
        {view === 'change-history' && (
          <TariffChangeHistory tariff={selectedTariff} />
        )}
      </Card>

      <AddBillingTariffModal
        editMode={false}
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
      />

      {selectedTariff && (
        <>
          <UpdateBillingTariffModal
            visible={updateModalVisible}
            onCancel={() => {
              setUpdateModalVisible(false)
              setSelectedTariff(null)
            }}
            tariff={selectedTariff}
          />
        </>
      )}
    </>
  )
}

export default ReferenceBookBillingPage

const LocalityTariffTable = ({
  data,
  onSeeChangeHistory,
  onEditOneTariff,
}: {
  data: IReferenceBillingTariffGroup
  onSeeChangeHistory: (tariff: IReferenceBillingTariff) => void
  onEditOneTariff: (tariff: IReferenceBillingTariff) => void
}) => {
  const { t } = useTranslation()
  const { localizeData } = useLocalization()
  const canManageTariff = rolesChecker(permissions)

  const [editModalVisible, setEditModalVisible] = useState(false)

  const getColumnsData = (title: string) => {
    const cols = [
      {
        title: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '40px',
            }}>
            <strong>{title}</strong>
            {canManageTariff && (
              <Button
                style={{
                  position: 'absolute',
                  right: '24px',
                }}
                type="primary"
                onClick={() => setEditModalVisible(true)}>
                {t('billing.change_tariff')}
              </Button>
            )}
          </div>
        ),
        children: [
          {
            title: '№',
            dataIndex: 'id',
            key: 'id',
            width: 50,
            render: (text, record) => (
              <div style={{ width: '50px' }}>{text}</div>
            ),
            align: 'center',
          },
          {
            title: t('billing.work_time'),
            key: 'work_time',
            width: 150,
            dataIndex: 'work_time',
          },
          {
            title: t('billing.one_child_sum'),
            key: 'one_child_sum',
            width: 200,
            dataIndex: 'one_child_sum',
            align: 'right',
            render: (text, record) => {
              return formatTiyinToSumLabel(text)
            },
          },
          {
            title: t('billing.two_children_sum'),
            key: 'two_children_sum',
            width: 200,
            dataIndex: 'two_children_sum',
            align: 'right',
            render: (text) => {
              return formatTiyinToSumLabel(text)
            },
          },
          {
            title: t('billing.status'),
            key: 'status',
            width: 150,
            dataIndex: 'status',
            render: (isActive, record) => {
              if (canManageTariff) {
                return <ChangeTariffStatus tariff={record.originalData} />
              }

              const color = isActive === true ? 'green' : 'volcano'
              const text =
                color === 'green' ? t('general.active') : t('general.inActive')
              return <Tag color={color}>{text}</Tag>
            },
          },
          {
            title: t('billing.last_change_date'),
            key: 'change_time',
            width: 200,
            dataIndex: 'change_time',
          },
        ],
      },
    ]

    if (canManageTariff) {
      cols[0].children.push({
        title: t('general.edit'),
        key: 'edit',
        width: 200,
        dataIndex: 'edit',
        render: (text, record) => (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button onClick={() => onEditOneTariff(record.originalData)}>
              {t('general.edit')}
            </Button>
            <Button onClick={() => onSeeChangeHistory(record.originalData)}>
              {t('billing.changeHistory')}
            </Button>
          </div>
        ),
      } as any)
    }

    return cols
  }

  const tableData = {
    localityData: data.localityData,
    tariffs: data.tariffs
      .map((item) => ({
        originalData: item,
        work_time: localizeData(item.organizationSchedule.data)?.name ?? '',
        one_child_sum: item.value,
        two_children_sum: item.discountedValue,
        status: item.status,
        change_time: item.lastUpdatedDate,
      }))
      .sort((a, b) => a.work_time.localeCompare(b.work_time))
      .map((i, index) => ({
        ...i,
        id: index + 1,
      })),
  }

  return (
    <>
      <br />
      <Table
        columns={getColumnsData(
          localizeData(tableData.localityData?.data)?.name ?? '',
        )}
        // columns={[
        //   {
        //     width: 12,

        //   }
        // ]}
        pagination={false}
        dataSource={tableData.tariffs}
      />
      <br />

      <AddBillingTariffModal
        editMode={true}
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        selectedTariffGroup={data}
      />
    </>
  )
}

const ChangeTariffStatus = ({
  tariff,
}: {
  tariff: IReferenceBillingTariff
}) => {
  const { t } = useTranslation()

  const [updateTariffStatus, { isLoading }] = useUpdateTariffStatusMutation()

  const onChangeTariffStatus = async (newStatus: boolean) => {
    try {
      await updateTariffStatus({
        id: tariff.id,
        status: newStatus,
      })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Spin
      spinning={isLoading}
      indicator={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}>
          <ThreeDots />
        </div>
      }>
      <Switch
        disabled={isLoading}
        checked={tariff.status}
        onChange={(newStatus) => onChangeTariffStatus(newStatus)}
        // color={isActive ? 'green' : 'volcano'}
        checkedChildren={t('general.active')}
        unCheckedChildren={t('general.inActive')}
      />
    </Spin>
  )
}
